import { nanoid } from 'nanoid';
import React, { ChangeEvent } from 'react';
import { Col, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { CancelButton, ConfirmButton } from '~/components/Buttons';
import { BuscaProduto, InputPeso } from '~/components/NovosInputs';
import { moedaFormatada } from '~/components/NovosInputs/InputMoney/formatMask';
import { useHandleFocus } from '~/pages/CadastroDePoducao/hook/useHandleFocus';
import { formatCurrencyAsText, transformAsCurrency } from '~/utils/functions';
import { insertOrUpdate } from '~/utils/masterDetail';
import { validaParaAdd } from '../../Validations/validaForSubmit';
import { calculaSubTotal } from '../../functions/calcSubTotal';
import { calcUnitario } from '../../functions/calcUnit';
import { parseDecimal } from '../../functions/formatToDecimal';
import { useCadastroProducao } from '../../hook/useCadastroProducao';
import { ProducaoRows } from '../../types';
import { TableProdutos } from './Table';

export const InputsProduto: React.FC = () => {
  const {
    formData,
    formProducao,
    updateItem,
    setUpdateItem,
    setProdutoIdUpdate,
    masterDetail,
    setMasterDetail,
    setProdutosRows,
    produtoIdUpdate,
    produtosRows,
    produtoAtual,
    setProdutoAtualReset,
    setProdutoAtual,
    qtdTotReceita,
    setQtdTotReceita,
    setCustoSemImposto,
    setValCustoFinal,
    setQtdTotProducao,
    setValCustoReceita,
    setQtdRendeKg,
    setValCustoProducao,
  } = useCadastroProducao();
  const {
    control,
    register,
    formState,
    setValue,
    handleSubmit,
    getValues,
    clearErrors,
  } = formProducao;
  const { errors } = formState;
  const { handleInputFocus } = useHandleFocus();

  const handleAddProdutoProducao = (selected: any) => {
    const data = selected.map((item: any) => {
      return {
        cod_seq_produto_producao: item.cod_produto,
        cod_loja: item.cod_loja,
        cod_produto: item.cod_produto,
        des_produto: item.des_produto,
        des_unidade_venda: item.des_unidade_venda,
        val_custo_rep: item.val_custo_rep,
        qtd_receita: item.qtd_receita,
        val_custo_receita: item.val_custo_receita,
        per_participacao: item.per_participacao,
        qtd_producao: item.qtd_producao,
        flg_ipv: item.flg_ipv,
        val_custo_producao: item.val_custo_producao,
        val_custo_sem_icms: item.val_custo_sem_icms,
        cod_associado: item.cod_associado,
      };
    });
    if (!data[0].flg_ipv) {
      toast.warn(`Não é possível inserir produtos de peso fixo.`);
      return handleInputFocus('busca_produto');
    }
    if (
      produtosRows?.some(
        (item: any) => item.cod_produto === data[0].cod_produto,
      )
    ) {
      const itemEncontrado = produtosRows.find(
        (item: any) => item.cod_produto === data[0].cod_produto,
      );

      setProdutoIdUpdate(itemEncontrado.uuid);
      setUpdateItem(true);
      setValue(
        'qtd_receita',
        formatCurrencyAsText(itemEncontrado.qtd_receita, 3, 3),
      );
      handleInputFocus('qtd_receita');
      setProdutoAtual(data[0]);
      return;
    }

    setProdutoAtual(data[0]);
    setValue('qtd_receita', '');
    return handleInputFocus('qtd_receita');
  };

  const handleAddProduto = handleSubmit(async (data: any) => {
    try {
      if (
        produtosRows?.some(
          (item: any) => item.cod_produto === produtoAtual.cod_produto,
        )
      ) {
        const itemEncontrado = produtosRows.find(
          (item: any) => item.cod_produto === produtoAtual.cod_produto,
        );

        setProdutoIdUpdate(itemEncontrado.uuid);
        setUpdateItem(true);
        return handleUpdateProduto();
      }
      const resValidation = validaParaAdd(data, produtoAtual);
      if (!resValidation.valid) return handleInputFocus(resValidation.campos);
      const { qtd_receita, qtd_rendimento } = getValues();

      const novaQtdTotReceita =
        parseDecimal(qtdTotReceita) + transformAsCurrency(qtd_receita);

      const resultCalcUnitario = calcUnitario({
        produtoAtual,
        qtdReceita: transformAsCurrency(qtd_receita),
        qtdRendimento: transformAsCurrency(qtd_rendimento),
        totQtdReceita: novaQtdTotReceita,
      });

      const uuid = nanoid();
      const insertRow: ProducaoRows = {
        uuid,
        id: uuid,
        cod_loja: produtoAtual.cod_loja,
        cod_produto: produtoAtual.cod_produto,
        des_produto: produtoAtual.des_produto,
        des_unidade: produtoAtual.des_unidade_venda,
        val_custo_rep: produtoAtual.val_custo_rep,
        val_custo_sem_icms: produtoAtual.val_custo_sem_icms,
        qtd_receita: transformAsCurrency(data.qtd_receita),
        qtd_producao: resultCalcUnitario.qtdProducao,
        flg_ipv: produtoAtual.flg_ipv,
        cod_associado: produtoAtual.cod_associado,
        qtd_rendimento: resultCalcUnitario.qtdRendeKg,
        custoSemImpostoFinal: resultCalcUnitario.custoSemImpostoFinal,
        custoFinal: resultCalcUnitario.custoFinal,
      };

      const rowsDetail = await insertOrUpdate(
        'produtos',
        insertRow,
        masterDetail,
        setMasterDetail,
      );

      setProdutosRows([...rowsDetail]);
      handleExecCalcSubTot([...rowsDetail]);
      clearForm();
    } catch (error) {
      toast.warning('Produto deve ser informado');
    }
  });

  const handleUpdateProduto = handleSubmit(async (data: any) => {
    if (produtoIdUpdate === undefined) return;
    const { qtd_receita, qtd_rendimento } = getValues();
    const resValidation = validaParaAdd(data, produtoAtual);

    if (!resValidation.valid) return handleInputFocus(resValidation.campos);

    const resultCalcUnitario = calcUnitario({
      produtoAtual,
      qtdReceita: transformAsCurrency(qtd_receita),
      qtdRendimento: transformAsCurrency(qtd_rendimento),
      totQtdReceita: qtdTotReceita + transformAsCurrency(qtd_receita),
    });

    const items = Array.from(produtosRows);
    const idx = items.findIndex((item) => item.id === produtoIdUpdate);

    const itemToUpdate: ProducaoRows = {
      ...items[idx],
      qtd_receita: transformAsCurrency(qtd_receita),
      qtd_producao: resultCalcUnitario.qtdProducao,
      qtd_rendimento: resultCalcUnitario.qtdRendeKg,
      custoSemImpostoFinal: resultCalcUnitario.custoSemImpostoFinal,
      custoFinal: resultCalcUnitario.custoFinal,
    };

    const rowsDetail = await insertOrUpdate(
      'produtos',
      itemToUpdate,
      masterDetail,
      setMasterDetail,
    );

    handleExecCalcSubTot([...rowsDetail]);
    setProdutosRows(rowsDetail);
    clearForm();
  });

  // calc SubTotais
  const handleExecCalcSubTot = (rowsDetail: any[]) => {
    const { per_despesa_producao } = getValues();
    const resultadoCalcSubTotal = calculaSubTotal({
      itens: rowsDetail,
      perDespesaProducao: transformAsCurrency(per_despesa_producao),
    });

    setQtdRendeKg(resultadoCalcSubTotal.rendeKg);
    setQtdTotProducao(resultadoCalcSubTotal.totQtdProducao);
    setQtdTotReceita(resultadoCalcSubTotal.totQtdReceita);
    setCustoSemImposto(resultadoCalcSubTotal.custoSemImp);
    setValCustoFinal(resultadoCalcSubTotal.custoFinalProducao);
    setValCustoReceita(resultadoCalcSubTotal.custoReceita);
    setValCustoProducao(resultadoCalcSubTotal.custoProducao);
    return resultadoCalcSubTotal;
  };

  const clearForm = () => {
    setUpdateItem(false);
    handleInputFocus('busca_produto');
    setProdutoAtualReset();
    setValue('qtd_receita', '');
    return setValue('busca_produto', '');
  };

  return (
    <>
      <Row>
        <Col sm={12} md={10}>
          <BuscaProduto
            tabIndex={0}
            label="Buscar Produto"
            placeholder="Selecione..."
            name="busca_produto"
            register={register}
            disabled={updateItem}
            isError={!!errors.busca_produto}
            control={control}
            buscaNasLojas={Number(formData.cod_loja)}
            setValue={setValue}
            customOptions={{
              hideBuscarPor: true,
              buscarPor: ['Produto'],
              filterByTipoMov: 0,
            }}
            getProduct={(selected: any) => {
              handleAddProdutoProducao(selected);
            }}
          />
        </Col>
        <Col sm={12} md={2}>
          <InputPeso
            id="qtd_receita"
            label="Qtd. Receita"
            name="qtd_receita"
            placeholder={produtoAtual?.flg_ipv ? '0,000' : '0'}
            decimals={3}
            max={9999}
            min={0.001}
            maxLength={12}
            control={control}
            register={register}
            isError={!!errors.qtd_receita}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setValue('qtd_receita', moedaFormatada(e.target.value, 3));
            }}
            onKeyPress={async (event: any) => {
              if (event.key === 'Enter') {
                if (updateItem) {
                  return handleUpdateProduto();
                }
                handleAddProduto();
              }
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12} md={12}>
          <Row style={{ marginTop: '10px', marginLeft: '15px' }}>
            <Col
              md={12}
              sm={12}
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <ConfirmButton
                onClick={() => {
                  if (updateItem) {
                    return handleUpdateProduto();
                  }
                  handleAddProduto();
                }}
              >
                Adicionar
              </ConfirmButton>

              <CancelButton
                onClick={() => {
                  clearForm();
                  clearErrors();
                  setUpdateItem(false);
                  setProdutoIdUpdate(undefined);
                }}
              >
                Cancelar
              </CancelButton>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col sm={12} md={12}>
          <TableProdutos />
        </Col>
      </Row>
    </>
  );
};
